import type { SearchResponse } from "@pokifolio/contracts";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApi } from "../../app/api-context";
import { useDebouncedValue } from "../../lib/use-debounced-value";

/** Wait this long after the last key press before looking cards up. */
export const LOOKUP_DELAY_MS = 300;
export const MIN_LOOKUP_LENGTH = 2;

/**
 * Card picker suggestions for what is being typed (D-63). Older lookups are cancelled as typing continues,
 * and the same words again use the cards already found.
 */
export function useCardLookup(text: string) {
  const api = useApi();
  const term = useDebouncedValue(text.trim(), LOOKUP_DELAY_MS);
  const enabled = term.length >= MIN_LOOKUP_LENGTH;
  const query = useQuery({
    queryKey: ["catalog", term],
    queryFn: ({ signal }) => api.searchCatalog(term, signal),
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
  return { ...query, term, enabled };
}

/** The last search results, kept while moving between pages (forgotten on sign-out). */
const LAST_SEARCH_KEY = ["search", "last"] as const;

/** Runs a search. Every run is a new, saved search, so it is a mutation rather than a cached query. */
export function useRunSearch() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.runSearch,
    onSuccess: (data) => queryClient.setQueryData(LAST_SEARCH_KEY, data),
  });
}

/**
 * The results of the last search, so coming back from Research shows them again
 * without searching (and spending a search) again.
 */
export function useLastSearch(): SearchResponse | undefined {
  return (
    useQuery<SearchResponse | null>({
      queryKey: LAST_SEARCH_KEY,
      queryFn: () => null,
      enabled: false,
      staleTime: Infinity,
      gcTime: 60 * 60_000,
    }).data ?? undefined
  );
}
